import { Box, Divider, Stack, Typography } from "@mui/material";
import { CustomizedBox } from "components/Custom/CustomizedBox";
import Chart from "react-google-charts";
import { useTranslation } from "react-i18next";
import EmptyChart from "../EmptyChart";
import { handleChartReady } from "utils/Global";
import { Link } from "react-router-dom";

type Props = {
  data: any;
};

const DeliveryTripChart = ({ data }: Props) => {
  const { t } = useTranslation();

  const formatData = [
    [
      t("logistic.delivery_trip.index"),
      t("status.booking"),
      t("status.wait_deliver"),
      t("status.delivering"),
      t("status.late"),
      t("status.finished"),
      t("status.cancelled"),
    ],
    [
      t("status.index"),
      data?.booking || 0,
      data?.wait_deliver || 0,
      data?.delivering || 0,
      data?.late || 0,
      data?.finished || 0,
      data?.cancelled || 0,
    ],
  ];

  const options = {
    chartArea: {
      width: "100%",
      height: "25%",
      top: 16,
    },
    isStacked: "percent",
    hAxis: { textPosition: "none", gridlines: { count: 0 } },
    vAxis: { textPosition: "none", gridlines: { count: 0 } },
    baselineColor: "transparent",
    legend: {
      position: "bottom",
      alignment: "start",
      textStyle: {
        fontName: "Kanit",
        fontSize: 12,
        color: "#737373",
      },
    },
    tooltip: {
      textStyle: {
        fontName: "Kanit",
        fontSize: 12,
        color: "#737373",
      },
    },
    bar: { groupWidth: "100%" },
    // 86d4889jb — 6th colour added for `booking`. This array is index-matched
    // to the series above; one short and the new bar silently falls back to a
    // Google default that clashes with the palette.
    colors: ["#5A6B7B", "#ED6C02", "#2167D3", "#F44336", "#4CAF50", "#737373"],
  };

  const total =
    (data?.booking ?? 0) +
    (data?.wait_deliver ?? 0) +
    (data?.delivering ?? 0) +
    (data?.late ?? 0) +
    (data?.finished ?? 0) +
    (data?.cancelled ?? 0);

  return (
    <CustomizedBox
      height={"100%"}
      border={`1px solid white`}
      margin={"0"}
      padding={"0.5rem 1rem"}
      bgcolor={"white"}
      boxShadow={"0px 4px 10px rgba(0, 0, 0, 0.05)"}
    >
      <Typography fontSize={16} fontWeight={600}>
        {t("logistic.delivery_trip.index")}
      </Typography>
      {total === 0 ? (
        <EmptyChart />
      ) : (
        <>
          <Stack
            direction="row"
            divider={<Divider orientation="vertical" flexItem />}
            spacing={2}
            mt={2}
          >
            <Link
              to={"/logistic/delivery-trip?filter=booking"}
              style={{ textDecoration: "none", flex: 1 }}
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography align="center" variant="h5" color={"primary.main"}>
                  {data?.booking || 0}
                </Typography>
                <Typography align="center" color={"#737373"}>
                  {t("status.booking")}
                </Typography>
              </Box>
            </Link>
            <Link
              to={"/logistic/delivery-trip?filter=wait_deliver"}
              style={{ textDecoration: "none", flex: 1 }}
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography align="center" variant="h5" color={"primary.main"}>
                  {data?.wait_deliver || 0}
                </Typography>
                <Typography align="center" color={"#737373"}>
                  {t("status.wait_deliver")}
                </Typography>
              </Box>
            </Link>
            <Link
              to={"/logistic/delivery-trip?filter=delivering"}
              style={{ textDecoration: "none", flex: 1 }}
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography align="center" variant="h5" color={"primary.main"}>
                  {data?.delivering || 0}
                </Typography>
                <Typography align="center" color={"#737373"}>
                  {t("status.delivering")}
                </Typography>
              </Box>
            </Link>
            <Link
              to={"/logistic/delivery-trip?filter=late"}
              style={{ textDecoration: "none", flex: 1 }}
            >
              <Box sx={{ cursor: "pointer" }}>
                <Typography align="center" variant="h5" color={"primary.main"}>
                  {data?.late || 0}
                </Typography>
                <Typography align="center" color={"#737373"}>
                  {t("status.late")}
                </Typography>
              </Box>
            </Link>
          </Stack>
          <Chart
            chartType="BarChart"
            width={"100%"}
            height={100}
            data={formatData}
            options={options}
            chartEvents={[
              {
                eventName: "ready",
                callback: ({ chartWrapper }: { chartWrapper: any }) =>
                  handleChartReady(chartWrapper),
              },
            ]}
          />
        </>
      )}
    </CustomizedBox>
  );
};

export default DeliveryTripChart;
